import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import { authService, authStorage } from "../auth";

interface RefreshResponse {
	accessToken: string;
	csrfToken: string;
}

interface CustomAxiosRequestConfig extends InternalAxiosRequestConfig {
	_retry?: boolean;
}

let refreshPromise: Promise<string> | null = null;

const baseURL =
	typeof import.meta !== "undefined" &&
		import.meta.env?.VITE_API_URL
		? import.meta.env.VITE_API_URL
		: process.env.NEXT_PUBLIC_PRACTIUM_API_URL;

const api = axios.create({
	baseURL,
});

api.interceptors.request.use((config) => {
	const token = authService.getToken();

	if (token) {
		config.headers.Authorization = `bearer ${token}`;
	}

	return config;
});

const refreshAccessToken = async () => {
	const currentToken = authStorage.getToken();

	if (!currentToken) {
		throw new Error("Sessão expirada.");
	}

	const csrfToken = authStorage.getCsrf();
	const headers: Record<string, string> = {
		Authorization: `bearer ${currentToken}`,
		Accept: "application/json",
	};

	if (csrfToken) {
		headers["x-csrf-token"] = csrfToken;
	}

	const refreshResponse = await axios.get<RefreshResponse>(`${baseURL}/refresh`, {
		withCredentials: true,
		headers,
	});

	const newAccessToken = refreshResponse.data.accessToken.replace(/^bearer\s+/i, "");
	authStorage.updateToken(newAccessToken);

	if (refreshResponse.data.csrfToken) {
		authStorage.saveCsrf(refreshResponse.data.csrfToken);
	}

	return newAccessToken;
};

api.interceptors.response.use(
	(response) => response,

	async (error: AxiosError<{ message?: string }>) => {
		const originalRequest = error.config as CustomAxiosRequestConfig | undefined;
		if (!originalRequest) {
			return Promise.reject(error);
		}

		if (error.response?.status === 403) {
			const message = error.response.data?.message || 'Você não tem permissão para realizar esta ação.';
			return Promise.reject(new Error(message));
		}

		const isUnauthorized = error.response?.status === 401;

		if (!isUnauthorized || originalRequest._retry || !authStorage.getToken()) {
			return Promise.reject(error);
		}

		originalRequest._retry = true;

		try {
			if (!refreshPromise) {
				refreshPromise = refreshAccessToken().finally(() => {
					refreshPromise = null;
				});
			}

			const newAccessToken = await refreshPromise;
			originalRequest.headers.Authorization = `bearer ${newAccessToken}`;

			return api(originalRequest);
		} catch (refreshError) {
			if (axios.isAxiosError(refreshError)) {
				const status = refreshError.response?.status;
				if (status === 401 || status === 406) {
					authStorage.clearSession();
				}
			}

			return Promise.reject(error);
		}
	}
);


export default api;
